/* © 2026 Wannlabs. ONE shared loading screen + skeletons. Flat (no card/window), themed by the host app through CSS
 * variables (--wl-load-bg / -fg / -muted / -accent / -bone, falling back to the chat-shell tokens), so Prime is teal,
 * Ember warm brown/orange, Wannlabs neutral. Polite status for screen readers, reduced-motion safe. */
import { Component, Suspense, type CSSProperties, type ReactNode } from "react";
import { isChunkError } from "./lazyRoute";

export function Skeleton({ w = "100%", h = 12, r = 8, className = "", style }: { w?: number | string; h?: number | string; r?: number; className?: string; style?: CSSProperties }) {
  return <span aria-hidden className={`wl-bone ${className}`} style={{ width: w, height: h, borderRadius: r, ...style }} />;
}

export type SkeletonLayout = "page" | "chat" | "list" | "viewer" | "panel";

function Bones({ layout }: { layout: SkeletonLayout }) {
  if (layout === "chat") return (
    <div className="wl-load__chat">
      {[78, 52, 64].map((w, i) => (
        <div key={i} className={`wl-load__row ${i % 2 ? "wl-load__row--me" : ""}`}><Skeleton w={`${w}%`} h={i % 2 ? 36 : 56} r={16} /></div>
      ))}
      <Skeleton className="wl-load__composer" h={48} r={24} />
    </div>
  );
  if (layout === "viewer") return <div className="wl-load__viewer"><Skeleton h={14} w="40%" /><Skeleton h={260} r={10} /></div>;
  if (layout === "list" || layout === "panel") return (
    <div className="wl-load__list">{Array.from({ length: layout === "panel" ? 3 : 6 }, (_, i) => (
      <div key={i} className="wl-load__item"><Skeleton w={36} h={36} r={18} /><span className="wl-load__lines"><Skeleton w="55%" /><Skeleton w="85%" h={10} /></span></div>
    ))}</div>
  );
  return (
    <div className="wl-load__page">
      <Skeleton w="32%" h={22} />
      <Skeleton w="60%" />
      <div className="wl-load__grid">{[0, 1, 2].map((i) => <Skeleton key={i} h={96} r={12} />)}</div>
      <Skeleton h={12} w="90%" /><Skeleton h={12} w="75%" />
    </div>
  );
}

export interface LoadingScreenProps { label?: string; layout?: SkeletonLayout; fullScreen?: boolean; className?: string }

/** Shown while a route/viewer chunk loads. fullScreen = first paint of the app; otherwise fills its container. */
export function LoadingScreen({ label = "Loading…", layout = "page", fullScreen = false, className = "" }: LoadingScreenProps) {
  return (
    <div className={`wl-load ${fullScreen ? "wl-load--full" : ""} ${className}`} role="status" aria-live="polite" aria-busy="true" data-testid="wl-loading">
      <span className="wl-load__bar" aria-hidden />
      <span className="wl-sr">{label}</span>
      <Bones layout={layout} />
    </div>
  );
}

interface BoundaryState { err: unknown }
/** Error boundary for lazy chunks: friendly retry instead of a white screen (non-chunk errors still show the message). */
export class LazyBoundary extends Component<{ children: ReactNode; onRetry?: () => void }, BoundaryState> {
  state: BoundaryState = { err: null };
  static getDerivedStateFromError(err: unknown) { return { err }; }
  render() {
    if (!this.state.err) return this.props.children;
    const chunk = isChunkError(this.state.err);
    return (
      <div className="wl-load wl-load--error" role="alert">
        <p className="wl-load__msg">{chunk ? "A new version of this app is ready." : "This part of the app didn't load."}</p>
        <button type="button" className="wl-load__btn" onClick={() => { this.setState({ err: null }); (this.props.onRetry ?? (() => window.location.reload()))(); }}>
          {chunk ? "Reload" : "Try again"}
        </button>
      </div>
    );
  }
}

/** Suspense + LazyBoundary + LoadingScreen in one wrapper for lazy routes. */
export function LazyGate({ children, layout = "page", label, fullScreen }: { children: ReactNode } & Omit<LoadingScreenProps, "className">) {
  return (
    <LazyBoundary>
      <Suspense fallback={<LoadingScreen layout={layout} label={label} fullScreen={fullScreen} />}>{children}</Suspense>
    </LazyBoundary>
  );
}
